import type { AccessStatus, MemberAccess } from './types';

/**
 * Acessos dos membros: a situação de cada um, os rótulos da tela e quem pode receber
 * convite. O servidor calcula a situação com `accessStatus`; a interface só a exibe.
 */

/** Prazo do link do convite, em dias (o e-mail e a tela de convite dizem "vale por 7 dias"). */
export const INVITE_DAYS = 7;

/** Ordem dos cards de filtro, do que está resolvido ao que falta fazer. */
export const ACCESS_ORDER: readonly AccessStatus[] = [
  'active',
  'invited',
  'expired',
  'notInvited',
  'noEmail',
];

export const ACCESS_STATUS: Record<AccessStatus, { label: string; description: string }> = {
  active: { label: 'Com acesso', description: 'Já criaram a senha e entram no Live Gospel.' },
  invited: { label: 'Convite enviado', description: 'Aguardando criar a senha pelo link.' },
  expired: { label: 'Convite expirado', description: 'O link venceu; envie um novo convite.' },
  notInvited: { label: 'Não convidado', description: 'Têm e-mail, mas ainda não receberam convite.' },
  noEmail: { label: 'Sem e-mail', description: 'Cadastre o e-mail no perfil para convidar.' },
};

/** Fatos guardados de um membro, de que a situação deriva. */
export interface AccessFacts {
  email: string;
  hasPassword: boolean;
  /** Validade do último convite ainda não usado; `null` sem convite pendente. */
  inviteExpiresAt: string | null;
}

export function accessStatus(facts: AccessFacts, now: Date): AccessStatus {
  if (facts.hasPassword) return 'active';
  if (!facts.email) return 'noEmail';
  if (!facts.inviteExpiresAt) return 'notInvited';

  return new Date(facts.inviteExpiresAt) > now ? 'invited' : 'expired';
}

/** Recebe convite quem tem e-mail e ainda não criou a senha (o convite pendente é refeito). */
export function canInvite(status: AccessStatus): boolean {
  return status === 'notInvited' || status === 'invited' || status === 'expired';
}

/** Quantos membros em cada situação. */
export function accessCounts(accesses: readonly MemberAccess[]): Record<AccessStatus, number> {
  const counts: Record<AccessStatus, number> = {
    active: 0,
    invited: 0,
    expired: 0,
    notInvited: 0,
    noEmail: 0,
  };

  for (const a of accesses) counts[a.status] += 1;

  return counts;
}
